import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
    EnvironmentsService,
    FileTransfersService,
    IOcbCUFileTransferRecipient,
    IOcbFileTransfer,
    IOcbFileTransferRecipient,
    IOcbNotificationGroup,
    NotificationsService,
    OcbFileTransferLogTypes,
    OcbRemotePlatformLogTypes
} from '@app/core';
import { TranslateService } from '@ngx-translate/core';
import { DxDataGridComponent } from 'devextreme-angular/ui/data-grid';
import { DxValidationGroupComponent } from 'devextreme-angular/ui/validation-group';
import { confirm } from 'devextreme/ui/dialog';
import notify from 'devextreme/ui/notify';
import { Observable, forkJoin, from as fromPromise } from 'rxjs';
import { filter, finalize, mergeMap, tap } from 'rxjs/operators';
import { FileTransferPickerComponent } from '../../shared';

@Component({
    selector: 'app-file-transfers-alerts',
    templateUrl: './file-transfers-alerts.component.html',
    styleUrls: ['./file-transfers-alerts.component.scss'],
})
export class FileTransfersAlertsComponent implements AfterViewInit {

    @ViewChild('recipientsGrid')
    public set recipientsGridPlaceholder(placeholder: DxDataGridComponent) {
        if (placeholder != null) this.recipientsGrid = placeholder;
    }

    @ViewChild('recipientValidation')
    public recipientValidationGroup: DxValidationGroupComponent;
    @ViewChild('fileTransferPicker')
    public fileTransferPicker: FileTransferPickerComponent;

    // List of file transfers
    public fileTransfers: IOcbFileTransfer[] = null;
    public selectedFileTransfer: IOcbFileTransfer = null;
    public createRecipientVisible = false;

    public groups: IOcbNotificationGroup[];
    public recipients: IOcbFileTransferRecipient[];
    public fileTransferLogTypes: string[];
    public fileTransferLogTypesLabels: any[];
    public remotePlatformLogTypes: string[];
    public remotePlatformLogTypesLabels: any[];

    public newRecipientData: IOcbCUFileTransferRecipient;
    public editedRecipients: IFileTransferRecipientUpdateObject[] = [];

    public isDropDownBoxOpened: boolean = false;

    public isLoadPanelVisible: boolean = true;
    public currentEnvironmentName: string = '';

    private currentEnvironmentId: string = '';
    private recipientsGrid: DxDataGridComponent;
    private requestedFileTransferId?: string;

    constructor(
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private environmentsService: EnvironmentsService,
        private fileTransfersService: FileTransfersService,
        private notifsService: NotificationsService) {
        this.displayFileTransferLogType = this.displayFileTransferLogType.bind(this);
        this.displayRemotePlatformLogType = this.displayRemotePlatformLogType.bind(this);
    }

    public ngAfterViewInit(): void {
        forkJoin([
            this.translateService.get('fileTransferLogTypes'),
            this.translateService.get('remotePlatformLogTypes')
        ]).subscribe({
            next: (messages) => {
                this.fileTransferLogTypesLabels = messages[0];
                this.remotePlatformLogTypesLabels = messages[1];
            },
            error: (err) => this.handleError(err)
        });
        this.route.queryParams.subscribe(queryParams => {
            if (queryParams['fileTransfer']) this.requestedFileTransferId = queryParams['fileTransfer'];
        });
        this.route.params.subscribe(_ => {
            this.currentEnvironmentId = this.route.snapshot.params.id;
            this.environmentsService.getEnvironment(this.currentEnvironmentId).subscribe(env => this.currentEnvironmentName = env.name);
            this.loadFileTransfers();
        });
    }

    public onContextMenuPreparing(e) {
        if (e.row && e.row.rowType === 'data') {
            this.translateService.get('_commons.delete').subscribe(messages => e.items = [{ text: messages[0], icon: 'trash', onItemClick: (ev) => this.deleteRecipient(ev, e.row.data) }]);
        }
    }

    public hideTagBoxDropdown(e): void { e.component.option('openOnFieldClick', false); }

    public showTagBoxDropButton(e): void { e.component.option('showDropDownButton', true); }

    // RECIPIENTS TAB //

    public showRecipientCreatePopup(): void {
        if (this.selectedFileTransfer) {
            this.newRecipientData = {
                emailAddress: null,
                userId: null,
                notificationGroupId: null,
                fileTransferLogTypesEnums: [],
                remotePlatformLogTypesEnums: [],
            };
            this.createRecipientVisible = true;
        } else {
            this.translateService.get('_pickers.msgFileTransferRequired')
            .subscribe(message => {
                notify(message, 'error', 3000);
            });
        }
    }

    public setRecipientFileTransferLogValue(recipient: IOcbFileTransferRecipient, value: OcbFileTransferLogTypes, e: { value: boolean }): void {
        if (e.value) {
            recipient.fileTransferLogTypesEnums.push(value);
        }
        else {
            const index = recipient.fileTransferLogTypesEnums.indexOf(value);
            recipient.fileTransferLogTypesEnums.splice(index, 1);
        }
        this.setEditedRecipient(recipient);
    }

    public setRecipientRemotePlatformLogValue(recipient: IOcbFileTransferRecipient, value: OcbRemotePlatformLogTypes, e: { value: boolean }): void {
        if (e.value) {
            recipient.remotePlatformLogTypesEnums.push(value);
        }
        else {
            const index = recipient.remotePlatformLogTypesEnums.indexOf(value);
            recipient.remotePlatformLogTypesEnums.splice(index, 1);
        }

        this.setEditedRecipient(recipient);
    }

    public createRecipient(): void {
        if (this.recipientValidationGroup.instance.validate().isValid) {
            this.createRecipientVisible = false;
            this.showLoading$(this.recipientsGrid, '_commons.loading')
                .pipe(
                    mergeMap(() => this.fileTransfersService.addRecipient(this.selectedFileTransfer.id, this.newRecipientData)),
                    tap(recipient => this.recipients.push(recipient)),
                    mergeMap(_ => this.translateService.get('alerts.msgAddRecipientComplete')),
                    finalize(() => this.recipientsGrid.instance.endCustomLoading()),
                )
                .subscribe({
                    next: (message) => notify(message, 'success', 3000),
                    error: (err) => this.handleError(err)
                });
        }
    }

    public updateRecipients(): void {
        // Create a service request Observable for each updated items
        const observables = [
            this.showLoading$(this.recipientsGrid, '_commons.loading'),
            ...this.editedRecipients.map(rec => this.fileTransfersService.updateRecipient(this.selectedFileTransfer.id, rec.id, rec.data)),
        ];

        forkJoin(observables).pipe(
            mergeMap(() => this.translateService.get('alerts.msgUpdateRecipientsComplete')),
            finalize(() => {
                this.editedRecipients = [];
                this.recipientsGrid.instance.endCustomLoading();
            })
        )
            .subscribe({
                next: (message) => notify(message, 'success', 3000),
                error: (err) => this.handleError(err)
            });
    }

    public deleteRecipient(e, recipient: IOcbFileTransferRecipient): void {
        e.event.stopPropagation();
        forkJoin([
            this.showLoading$(this.recipientsGrid, '_commons.loading'),
            this.translateService.get('alerts.msgDeleteRecipientConfirm', { name: recipient.displayName ? recipient.displayName : recipient.emailAddress }),
            this.translateService.get('alerts.msgDeleteRecipientConfirmTitle')
        ]).pipe(
            mergeMap(messages => fromPromise(confirm(messages[1], messages[2]))),
            filter(accepted => accepted),
            mergeMap(_ => this.fileTransfersService.deleteRecipient(recipient.fileTransferId, recipient.id)),
            tap(_ => {
                const index = this.recipients.indexOf(recipient);
                this.recipients.splice(index, 1);
            }),
            mergeMap(_ => this.translateService.get('alerts.msgDeleteRecipientComplete')),
            finalize(() => this.recipientsGrid.instance.endCustomLoading())
        ).subscribe({
            next: (message) => notify(message, 'success', 3000),
            error: (err) => this.handleError(err)
        });
    }

    public filterChanged(e): void {
        if (e) {
            this.selectedFileTransfer = e;
            this.isDropDownBoxOpened = false;
            this.loadData();
        } else {
            this.recipients = [];
            this.groups = [];
        }
    }

    public displayRecipientName(recipient: IOcbFileTransferRecipient): string { return recipient.displayName ? recipient.displayName : recipient.emailAddress; }

    public displayFileTransferLogType(type: string): string { return this.fileTransferLogTypesLabels[type]; }

    public displayRemotePlatformLogType(type: string): string { return this.remotePlatformLogTypesLabels[type]; }

    private setEditedRecipient(recipient: IOcbFileTransferRecipient): void {
        // Create a CU object and copy current data
        const cuRecipient = {
            id: recipient.id,
            environmentId: recipient.environmentId,
            data: {
                environmentId: recipient.environmentId,
                fileTransferId: recipient.fileTransferId,
                emailAddress: recipient.emailAddress,
                userId: recipient.user ? recipient.user.id : undefined,
                notificationGroupId: recipient.notificationGroupId,
                fileTransferLogTypesEnums: recipient.fileTransferLogTypesEnums,
                remotePlatformLogTypesEnums: recipient.remotePlatformLogTypesEnums,
            },
        };

        const editIndex = this.editedRecipients.findIndex(cuRec => cuRec.id === cuRecipient.id);
        if (editIndex > -1) this.editedRecipients[editIndex] = cuRecipient;
        else this.editedRecipients.push(cuRecipient);
    }

    private loadFileTransfers(): void {
        this.currentEnvironmentId = this.route.snapshot.params.id;
        this.fileTransfersService.getFileTransfers(this.currentEnvironmentId, true)
            .pipe(
                finalize(() => this.isLoadPanelVisible = false))
            .subscribe({
                next: (res) => {
                    this.fileTransfers = res;
                    if (this.requestedFileTransferId) this.fileTransferPicker.selectFileTransfer(this.requestedFileTransferId);
                },
                error: (err) => this.handleError(err)
            });
    }

    private loadData(): void {
        if (this.selectedFileTransfer) {
            // Load notification groups
            this.showLoading$(this.recipientsGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.notifsService.getNotificationGroups(this.currentEnvironmentId)),
                    finalize(() => this.recipientsGrid.instance.endCustomLoading()),
                )
                .subscribe({
                    next: (res) => this.groups = res,
                    error: (err) => this.handleError(err)
                });

            // Load recipients
            this.showLoading$(this.recipientsGrid, '_commons.loading')
                .pipe(
                    mergeMap(_ => this.fileTransfersService.getRecipients(this.selectedFileTransfer.id)),
                    finalize(() => this.recipientsGrid.instance.endCustomLoading()),
                )
                .subscribe({
                    next: (res) => this.recipients = res,
                    error: (err) => this.handleError(err)
                });

            // Get available enum values
            this.fileTransferLogTypes = Object.keys(OcbFileTransferLogTypes).filter(key => typeof (OcbFileTransferLogTypes[key]) === 'number');
            this.remotePlatformLogTypes = Object.keys(OcbRemotePlatformLogTypes).filter(key => typeof (OcbRemotePlatformLogTypes[key]) === 'number');
        }
        else {
            this.translateService.get('_pickers.msgFileTransferRequired').subscribe(message => notify(message, 'error', 3000));
        }
    }

    private showLoading$(grid: DxDataGridComponent, messageKey: string): Observable<void> {
        return this.translateService.get(messageKey).pipe(
            tap(message => {
                if (grid != null) grid.instance.beginCustomLoading(`${message}...`);
            }),
        );
    }

    private handleError(error: any): void {
        let message: '';
        if (error instanceof HttpErrorResponse) {
            if (error.error.hasOwnProperty('message')) message = error.error.message;
            else if (error.error.hasOwnProperty('name')) message = error.error.name;
        }
        else message = error;

        notify(message, 'error', 3000);
    }
}

interface IFileTransferRecipientUpdateObject {
    id: string;
    environmentId: string;
    data: IOcbFileTransferRecipient;
}
